/**
 * SupportRequestForm
 *
 * "Different format? Describe your data format and we'll help set up a proper import."
 * Sends message (+ optional sample file) to backend which stores in R2 and emails support.
 * Name and email are derived server-side from the authenticated user session.
 *
 * The request is always tied to a target organization (where the data should be
 * imported into). Single-org users have it auto-attached; multi-org users must pick.
 */
import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Form, Input, Select, Spin, Typography, Upload} from 'antd';
import {InboxOutlined} from '@ant-design/icons';
import type {RcFile, UploadFile} from 'antd/es/upload';
import {FormattedMessage, useIntl} from 'react-intl';
import {getAuthenticationApi, getPatientImportApi} from '../../api/apiInstances';
import {OrganizationMembershipResponse} from '../../api-client';
import logger from '../../utils/logger';

const {TextArea} = Input;
const {Title, Paragraph} = Typography;
const {Dragger} = Upload;

const SUPPORT_MAX_BYTES = 5 * 1024 * 1024;
const SUPPORT_ALLOWED_EXT = ['.csv', '.xlsx', '.xls', '.xml'];

interface FormValues {
    message: string;
    targetOrganizationId?: string;
}

export const SupportRequestForm: React.FC = () => {
    const intl = useIntl();
    const [form] = Form.useForm<FormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);

    // Target organizations the user belongs to (active memberships only).
    const [orgs, setOrgs] = useState<OrganizationMembershipResponse[]>([]);
    const [orgsLoading, setOrgsLoading] = useState(true);
    const [orgsError, setOrgsError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setOrgsLoading(true);
        setOrgsError(false);
        getAuthenticationApi()
            .getUserOrganizations()
            .then(res => {
                if (cancelled) return;
                const active = (res.data as OrganizationMembershipResponse[]).filter(
                    o => o.status?.toLowerCase() === 'active'
                );
                setOrgs(active);
            })
            .catch(err => {
                if (cancelled) return;
                logger.error('Failed to load organizations for import support form:', err);
                setOrgsError(true);
            })
            .finally(() => {
                if (!cancelled) setOrgsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const singleOrg = orgs.length === 1;
    const multiOrg = orgs.length > 1;
    // Block submission until we can guarantee a valid target organization.
    const submitDisabled = orgsLoading || orgsError || orgs.length === 0;

    const beforeUpload = (file: RcFile): boolean => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!SUPPORT_ALLOWED_EXT.includes(ext)) {
            setFileError(
                intl.formatMessage(
                    {id: 'import_support_file_hint'},
                    // Override with allowed list info from error response
                )
            );
            return false;
        }
        if (file.size > SUPPORT_MAX_BYTES) {
            setFileError(intl.formatMessage({id: 'import_file_too_large'}));
            return false;
        }
        setFileError(null);
        return false; // prevent antd auto-upload
    };

    const handleSubmit = async (values: FormValues) => {
        // Single-org users have no dropdown — auto-attach their only org.
        const targetOrganizationId = singleOrg
            ? orgs[0].organizationId
            : values.targetOrganizationId;
        if (!targetOrganizationId) {
            setError(intl.formatMessage({id: 'import_support_org_load_error'}));
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const api = getPatientImportApi();
            const attachedFile = fileList[0]?.originFileObj ?? undefined;
            await api.submitImportSupportRequest(values.message, targetOrganizationId, attachedFile);
            setSubmitted(true);
            form.resetFields();
            setFileList([]);
        } catch (e: any) {
            const msg =
                e?.response?.data?.detail ||
                intl.formatMessage({id: 'import_support_error'});
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <Alert
                type="success"
                message={<FormattedMessage id="import_support_sent"/>}
                description={<FormattedMessage id="import_support_sent_desc"/>}
                showIcon
                style={{marginTop: 24}}
            />
        );
    }

    return (
        <Card style={{maxWidth: 600}} bordered={false}>
            <Title level={4} style={{marginBottom: 4}}>
                <FormattedMessage id="import_support_title"/>
            </Title>
            <Paragraph type="secondary" style={{marginBottom: 24}}>
                <FormattedMessage id="import_support_desc"/>
            </Paragraph>

            {error && (
                <Alert
                    type="error"
                    message={error}
                    closable
                    onClose={() => setError(null)}
                    style={{marginBottom: 16}}
                />
            )}

            {orgsError && (
                <Alert
                    type="error"
                    message={<FormattedMessage id="import_support_org_load_error"/>}
                    showIcon
                    style={{marginBottom: 16}}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
            >
                {multiOrg && (
                    <Form.Item
                        name="targetOrganizationId"
                        label={<FormattedMessage id="import_support_org_label"/>}
                        rules={[{required: true, message: <FormattedMessage id="field_required"/>}]}
                    >
                        <Select
                            placeholder={intl.formatMessage({id: 'import_support_org_placeholder'})}
                            options={orgs.map(o => ({
                                value: o.organizationId!,
                                label: o.organizationName,
                            }))}
                        />
                    </Form.Item>
                )}

                <Form.Item
                    name="message"
                    label={<FormattedMessage id="import_support_message_label"/>}
                    rules={[{required: true, message: <FormattedMessage id="field_required"/>}]}
                >
                    <TextArea
                        rows={6}
                        placeholder={intl.formatMessage({id: 'import_support_message_placeholder'})}
                    />
                </Form.Item>

                <Form.Item
                    label={<FormattedMessage id="import_support_file_label"/>}
                    validateStatus={fileError ? 'error' : undefined}
                    help={fileError ?? undefined}
                >
                    <Dragger
                        fileList={fileList}
                        beforeUpload={beforeUpload}
                        onChange={({fileList: fl}) => setFileList(fl.slice(-1))}
                        onRemove={() => {
                            setFileList([]);
                            setFileError(null);
                        }}
                        maxCount={1}
                        accept=".csv,.xlsx,.xls,.xml"
                        style={{padding: '8px 0'}}
                    >
                        <p className="ant-upload-drag-icon" style={{marginBottom: 4}}>
                            <InboxOutlined style={{fontSize: 24}}/>
                        </p>
                        <p className="ant-upload-text" style={{fontSize: 13}}>
                            <FormattedMessage id="import_support_file_hint"/>
                        </p>
                        <p className="ant-upload-hint" style={{fontSize: 11}}>
                            .csv · .xlsx · .xls · .xml — max 5 MB
                        </p>
                        <p className="ant-upload-hint" style={{fontSize: 11, color: '#888', marginTop: 4}}>
                            These files are for support review only and are not directly imported.
                            Direct import supports .csv and .xlsx only.
                        </p>
                    </Dragger>
                </Form.Item>

                <Form.Item style={{marginBottom: 0}}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        disabled={submitDisabled}
                        block
                    >
                        {orgsLoading ? <Spin size="small"/> : <FormattedMessage id="import_support_submit"/>}
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};
